import { useState, Dispatch, SetStateAction, memo, useMemo } from 'react';
import { useQueryClient, useQuery, useMutation } from 'react-query';
import CallQueueAPI from '@frontend/api-call-queue';
import SipProfileAPI from '@frontend/api-sip-profile';
import { scopedFilterAndSort } from '@frontend/filters';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { SettingsTenantLocation, usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import {
  useAlert,
  ModalControlModalProps,
  Modal,
  Table,
  Chip,
  Text,
  SearchField,
  useFormField,
} from '@frontend/design-system';
import { useCallQueueCacheUpdater } from '../../hooks/useCallQueueCacheUpdater';
import { queryKeys } from '../../query-keys';
import { trackingId } from '../../tracking';
import { logError } from '../../utils/loggers';
import { PhoneSettingsScopedLocationFilterMenu } from '../common/index-table';
import { SettingsCard } from '../common/settings-card';
import { CallQueueDevice, CallQueueDeviceUpdatePayload, CallQueueState, DeviceType } from './types';

export const useAssignDeviceAPI = (tenantLocation: SettingsTenantLocation) => {
  const { t } = useTranslation('phone');

  const alerts = useAlert();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [tenantLocation.phoneTenantId, ...queryKeys.settings.listAvailableDevices()],
    queryFn: () => SipProfileAPI.List({ tenantId: tenantLocation.phoneTenantId }),
    select: (data) => {
      return data.sipProfiles
        ? data.sipProfiles.reduce((acc, profile) => {
            if (profile.device && profile.device.deviceType !== DeviceType.MOBILE_APP) {
              acc.push({
                sipProfileId: profile.sipProfileId,
                macAddress: profile.device.macAddress ?? '',
                // We expose the sip profile name to users, not the device name
                name: profile.name ?? '',
                locationIds: profile.device.location ? [profile.device.location.locationId] : [],
                deviceId: profile.device.deviceId ?? '',
              });

              return acc;
            } else {
              return acc;
            }
          }, [] as CallQueueDevice[])
        : [];
    },
    staleTime: 5 * 60 * 1000,
    meta: {
      error: {
        message: 'useAssignDeviceAPI:query | Failed to fetch available devices',
        data: { phoneTenantId: tenantLocation.phoneTenantId },
      },
    },
  });

  const mutation = useMutation({
    mutationFn: (payload: CallQueueDeviceUpdatePayload) => CallQueueAPI.UpdateDevices(payload),
    onMutate: async (payload) => {
      const queryKey = [tenantLocation.phoneTenantId, ...queryKeys.settings.readCallQueue(payload.callQueueId)];

      await queryClient.cancelQueries({ queryKey });

      const previousCallQueue = queryClient.getQueryData(queryKey);
      queryClient.setQueryData<CallQueueState>(queryKey, (old) => {
        if (!old) {
          return {} as CallQueueState;
        }
        return { ...old, devices: payload.devices };
      });

      return { previousCallQueue };
    },
    onSuccess: () => {
      alerts.success(t('Device assignments updated successfully.'));
    },
    onError: (err, payload, context) => {
      const queryKey = [tenantLocation.phoneTenantId, ...queryKeys.settings.readCallQueue(payload.callQueueId)];

      alerts.error(t('System failed to update device assignments. Please try again.'));
      queryClient.setQueryData(queryKey, context?.previousCallQueue);
      logError('useAssignDeviceAPI:mutation | Failed to update device assignments', {
        data: { phoneTenantId: tenantLocation.phoneTenantId, callQueueId: payload.callQueueId, error: err },
      });
    },
    onSettled: (_data, _err, payload) => {
      const readQueryKey = [tenantLocation.phoneTenantId, ...queryKeys.settings.readCallQueue(payload.callQueueId)];
      const listQueryKey = [tenantLocation.phoneTenantId, ...queryKeys.settings.listCallQueue()];

      queryClient.invalidateQueries({ queryKey: readQueryKey });
      queryClient.invalidateQueries({ queryKey: listQueryKey });
    },
  });

  return { query, mutation };
};

const LimitMessage = ({ show }: { show: boolean }) => {
  const { t } = useTranslation('phone');

  return show ? (
    <div style={{ display: 'flex', gap: theme.spacing(1), alignItems: 'center' }}>
      <Icon name='info-small' />
      <Text color='subdued' size='small'>
        {t('You can only have a maximum of 30 devices.')}
      </Text>
    </div>
  ) : null;
};

export const AssignDeviceModal = ({
  modalProps,
  callQueueId,
  assignedDevices,
  preSelectedDevices,
  setPreSelectedDevices,
  updateDevices,
  tenantLocation,
  onDeferredSave,
}: {
  modalProps: ModalControlModalProps;
  callQueueId: string;
  assignedDevices: CallQueueDevice[];
  preSelectedDevices: CallQueueDevice[];
  setPreSelectedDevices: (devices: CallQueueDevice[]) => void;
  updateDevices: (devices: CallQueueDevice[]) => void;
  tenantLocation: SettingsTenantLocation;
  /**
   * When provided, device selections are not persisted immediately via the UpdateDevices API.
   * Instead, the resulting device list is handed back to the caller to be included in the
   * next batched save.
   */
  onDeferredSave?: (devices: CallQueueDevice[]) => void;
}) => {
  const { t } = useTranslation('phone');
  const { query, mutation } = useAssignDeviceAPI(tenantLocation);
  const { updateCallRouteCaches } = useCallQueueCacheUpdater(callQueueId);

  const resetState = (reset: boolean) => {
    if (reset) {
      setPreSelectedDevices(assignedDevices);
      modalProps.onClose();
    }
  };

  return (
    <Modal minWidth={800} {...modalProps} onClose={() => resetState(true)}>
      <Modal.Header onClose={() => resetState(true)}>{t('Assign Devices')}</Modal.Header>
      <Modal.Body css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
        <Text>{t('Assign devices to this call queue to ring when a call comes in.')}</Text>
        <AssignDeviceTable
          assignedDevices={preSelectedDevices}
          onSelectionChange={setPreSelectedDevices}
          availableDevices={query.data ?? []}
          isLoading={query.isLoading}
          tenantLocation={tenantLocation}
        />
        <LimitMessage show={preSelectedDevices.length > 30} />
      </Modal.Body>
      <Modal.Actions
        primaryLabel={t('Save')}
        disablePrimary={preSelectedDevices.length > 30}
        onPrimaryClick={() => {
          if (onDeferredSave) {
            updateDevices(preSelectedDevices);
            onDeferredSave(preSelectedDevices);
            modalProps.onClose();
            return;
          }

          const deviceNames = preSelectedDevices?.map((device) => device.name).filter(Boolean) || [];

          updateDevices(preSelectedDevices);
          mutation.mutate({ callQueueId, devices: preSelectedDevices });

          updateCallRouteCaches({
            deviceNames,
            deviceCount: deviceNames.length,
          });

          modalProps.onClose();
        }}
        primaryTrackingId={trackingId({
          context: 'setting',
          feature: 'call-queues',
          details: 'save-assigned-devices',
        })}
      />
    </Modal>
  );
};

// This memo ensures that the table does not re-render when the parent component re-renders with the same props passed to the table.
const AssignDeviceTable = memo(
  ({
    assignedDevices,
    availableDevices,
    onSelectionChange,
    isLoading,
    tenantLocation,
  }: {
    assignedDevices: CallQueueDevice[];
    availableDevices: CallQueueDevice[];
    onSelectionChange: (devices: CallQueueDevice[]) => void;
    isLoading?: boolean;
    tenantLocation: SettingsTenantLocation;
  }) => {
    const { t } = useTranslation('phone');
    const { getScopeName } = useAppScopeStore();
    const { globalAvailableLocationIds } = usePhoneSettingsShallowStore('globalAvailableLocationIds');
    const searchField = useFormField({ type: 'text' });
    const [filteredLocations, setFilteredLocations] = useState<string[]>([]);

    const filteredAvailableDevices = useMemo(() => {
      const devicesFilteredByLocations = scopedFilterAndSort({
        data: availableDevices,
        scopeIds: globalAvailableLocationIds,
        filteredScopeIds: filteredLocations,
      });

      return devicesFilteredByLocations.filter((device) =>
        device.name.toLowerCase().includes(searchField.value.toLowerCase())
      );
    }, [searchField.value, availableDevices, filteredLocations]);
    const isUnify = tenantLocation && tenantLocation.locationType === 'unify';

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          {tenantLocation?.locationType !== 'single' ? (
            <PhoneSettingsScopedLocationFilterMenu
              selectedOptions={filteredLocations}
              onChange={setFilteredLocations}
            />
          ) : null}
          <SearchField css={{ width: theme.spacing(40) }} {...searchField} name='device-search' />
        </div>
        <Table
          isPaginated
          rowSelectionConfig={{
            hideBulkSelection: true,
            initialState: Object.fromEntries(assignedDevices.map((device) => [device.sipProfileId, true])),
          }}
          isSelectable
          fullHeight
          fullHeightConfig={{
            maxHeight: 300,
            minHeight: 200,
          }}
          isLoading={isLoading}
          data={filteredAvailableDevices ?? []}
          colConfig={[
            { id: 'name', accessor: 'name', Header: t('Device Name') },
            { id: 'macAddress', accessor: 'macAddress', Header: t('MAC Address'), width: 150 },
            {
              id: 'location',
              Header: t('Location'),
              accessor: (data) => {
                const locationIds = data.locationIds;
                if (!locationIds) {
                  return null;
                }
                if (locationIds.length === 1) {
                  return { label: getScopeName(locationIds[0]), type: 'single' };
                } else if (locationIds.length > 1) {
                  return { label: t('{{count}} locations', { count: locationIds.length }), type: 'multi' };
                } else {
                  return { label: '--', type: 'none' };
                }
              },
              cellRenderer: (data: { label: string; type: 'multi' | 'single' | 'none' }) => {
                if (data.type === 'none') {
                  return data.label;
                }
                return data.type === 'multi' ? (
                  <Chip.MultiChip>{data.label}</Chip.MultiChip>
                ) : (
                  <Chip.SingleChip>{data.label}</Chip.SingleChip>
                );
              },
              width: 150,
              omit: !isUnify,
              sortType: (a, b) => {
                return a.values.labels.label.localeCompare(b.values.labels.label);
              },
            },
          ]}
          uniqueRowId={(row) => row.sipProfileId}
          rowActions={{
            onRowSelect: (row) => {
              const newDevices = [...assignedDevices];
              if (!row.isSelected) {
                newDevices.push(row.original);
              } else {
                const removalIndex = newDevices.findIndex((device) => device.deviceId === row.original.deviceId);
                if (removalIndex > -1) {
                  newDevices.splice(removalIndex, 1);
                }
              }
              onSelectionChange(newDevices);
            },
          }}
        />
      </div>
    );
  }
);

AssignDeviceTable.displayName = 'AssignDeviceTable';

export const AssignDeviceStepCard = ({
  selectedDeviceCount,
  assignedDevices,
  tenantLocation,
  setState,
}: {
  selectedDeviceCount: number;
  assignedDevices: CallQueueDevice[];
  tenantLocation: SettingsTenantLocation;
  setState: Dispatch<SetStateAction<CallQueueState>>;
}) => {
  const { t } = useTranslation('phone');
  const { query } = useAssignDeviceAPI(tenantLocation);

  const updateDevices = (devices: CallQueueDevice[]) => {
    setState((state) => ({ ...state, devices: devices }));
  };

  return (
    <SettingsCard title={t('Assign Devices')} condensed>
      <AssignDeviceTable
        assignedDevices={assignedDevices}
        onSelectionChange={updateDevices}
        availableDevices={query.data ?? []}
        isLoading={false}
        tenantLocation={tenantLocation}
      />
      <LimitMessage show={selectedDeviceCount > 30} />
    </SettingsCard>
  );
};
